import { MinusCircleIcon, PlusCircleIcon } from '@grupoboticario/flora-react-icons';
import { ChooseItem } from 'core/services/bff';
import { actions } from 'core/use-cases/checkout';
import { useCommand } from 'core/use-cases/utilities';
import useQuantitySelector from 'ui/components/templates/QuantitySelector/QuantitySelector.hooks';
import QuantitySelector from 'ui/components/templates/QuantitySelector/QuantitySelector.styles';

import type { SelectedPromotionItemQuantity } from 'core/domains/checkout';
import type { QuantitySelectorProps } from 'ui/components/templates/QuantitySelector';

const GiftsCarrouselQuantitySelector = ({
  disabled = false,
  max = 9999,
  min = 0,
  onChange,
  size,
  value = 0,
  item,
}: QuantitySelectorProps & { item: { gift: ChooseItem; sku: string; index: number } }) => {
  const { currentValue, handleChange } = useQuantitySelector({
    max,
    min,
    onChange,
    value,
  });

  const isMinimumDisabled = disabled || currentValue === min;
  const isMaximumDisabled = disabled || currentValue === max;

  const { dispatchCommand } = useCommand();
  const handleUpdateItem = (
    {
      gift: {
        giftQuantity,
        giftNumber,
        giftItemNumber,
        nextOrderAllowed,
        promotion: { code },
        items,
      },
      sku,
      index,
    }: SelectedPromotionItemQuantity,
    method: string,
    quantity?: number,
  ) =>
    dispatchCommand(
      actions.giftItemUpdate({
        index,
        method,
        giftQuantity,
        giftNumber,
        giftItemNumber,
        promotionCode: code,
        nextOrderAllowed,
        rewards: [
          {
            name: items.find((selected) => item.sku === selected.sku)?.name || '',
            sku,
            quantity: quantity || 0,
            images: items.find((selected) => item.sku === selected.sku)?.images || [],
          },
        ],
      }),
    );

  return (
    <QuantitySelector.Component data-size={size} data-testid="quantity-selector">
      <QuantitySelector.Button
        type="button"
        data-testid="button-minus"
        disabled={isMinimumDisabled}
        onClick={() => handleUpdateItem(item, 'remove')}
        stretch
      >
        <MinusCircleIcon size={24} />{' '}
      </QuantitySelector.Button>

      <QuantitySelector.InputQuantity
        data-testid="input-quantity"
        disabled={disabled}
        id="inputQuantity"
        max={max}
        min={min}
        name="input-quantity"
        onChange={(e) => {
          const target = e.target;
          const inputValue = target.value;

          handleChange(e);
          handleUpdateItem(item, 'overwrite', Number(inputValue));
          target.select();
        }}
        type="number"
        value={Number(currentValue).toFixed()}
        onFocus={(e) => {
          e.target.select();
        }}
      />

      <QuantitySelector.Button
        type="button"
        data-testid="button-plus"
        disabled={isMaximumDisabled}
        onClick={() => handleUpdateItem(item, 'add')}
        stretch
      >
        <PlusCircleIcon size={24} />
      </QuantitySelector.Button>
    </QuantitySelector.Component>
  );
};

export default GiftsCarrouselQuantitySelector;
